// Azzurro "Common Expressions" pillar — figurative phrases whose meaning differs
// from their literal words (e.g. `avere voglia di` literally "to have desire of",
// but really "to feel like"). These trip up learners precisely because the
// word-for-word reading is misleading, so each card shows three layers:
//
//   1. the expression,
//   2. what it *sounds like* word-for-word (the literal trap), and
//   3. what it *actually means*.
//
// This is a STATIC local-data pillar (like tenses / reference) — no PocketBase
// collection, no seed, no pins/notes. It ships entirely in the bundle.
//
// Each row is a terse tuple so the data reads like a table:
//   [ italian, literal_gloss, meaning, example_it, example_en, emoji ]
// (The object keys stay `spanish` / `example_es` — the codebase-wide
// target-language field names — but they hold Italian.)
// The literal_gloss is the deliberately-awkward word-for-word reading; meaning
// is the natural English. Groupings are by the anchoring verb so learners see
// the family resemblance (all the `dare …` phrases together, etc.).

const GROUPS = [
  // ---- avere (to have) -------------------------------------------------
  {
    category: "avere", rows: [
      ["avere voglia di", "to have desire of", "to feel like / to fancy", "Ho voglia di un caffè.", "I feel like a coffee.", "🤩"],
      ["avere fretta", "to have hurry", "to be in a hurry", "Ho fretta, ci sentiamo dopo.", "I'm in a hurry, let's talk later.", "⏱️"],
      ["avere ragione", "to have reason", "to be right", "Hai ragione, mi sono sbagliato.", "You're right, I was wrong.", "✅"],
      ["avere bisogno di", "to have need of", "to need", "Ho bisogno di una mano.", "I need a hand.", "🧩"],
      ["avere paura di", "to have fear of", "to be afraid of", "Ho paura dei ragni.", "I'm afraid of spiders.", "😨"],
      ["avere sonno", "to have sleep", "to be sleepy", "I bambini hanno sonno.", "The children are sleepy.", "😴"],
    ],
  },

  // ---- fare (to do / make) --------------------------------------------
  {
    category: "fare", rows: [
      ["fare attenzione", "to make attention", "to pay attention / be careful", "Fai attenzione al gradino.", "Mind the step.", "⚠️"],
      ["fare la fila", "to make the line", "to queue / to stand in line", "Abbiamo fatto la fila per un'ora.", "We queued for an hour.", "🧍"],
      ["fare finta di", "to make pretence of", "to pretend (to)", "Fa finta di non capire.", "He pretends not to understand.", "🙄"],
      ["fare il ponte", "to make the bridge", "to take a long weekend", "Venerdì facciamo il ponte.", "We're taking Friday off for a long weekend.", "🌉"],
      ["fare caso a", "to make case to", "to notice / to pay heed to", "Non ho fatto caso al cartello.", "I didn't notice the sign.", "👀"],
      ["farcela", "to make it there", "to manage / to make it", "Ce l'ho fatta!", "I made it!", "🏁"],
    ],
  },

  // ---- dare (to give) --------------------------------------------------
  {
    category: "dare", rows: [
      ["dare fastidio", "to give annoyance", "to bother / to annoy", "La musica alta dà fastidio.", "Loud music is annoying.", "😤"],
      ["dare una mano", "to give a hand", "to lend a hand / to help out", "Mi dai una mano?", "Can you give me a hand?", "✋"],
      ["dare retta a", "to give heed to", "to listen to / to heed", "Dai retta a tua madre.", "Listen to your mother.", "👂"],
      ["darsi da fare", "to give oneself to do", "to get busy / to make an effort", "Datti da fare!", "Get a move on!", "💪"],
      ["dare alla luce", "to give to the light", "to give birth", "Ha dato alla luce due gemelli.", "She gave birth to twins.", "👶"],
      ["dare per scontato", "to give for discounted", "to take for granted", "Lo davo per scontato.", "I took it for granted.", "🤷"],
    ],
  },

  // ---- mettere / mettersi (to put) ------------------------------------
  {
    category: "mettere", rows: [
      ["mettersi a", "to put oneself to", "to start (doing) / to set about", "Si è messo a piovere.", "It started to rain.", "🌧️"],
      ["mettere in dubbio", "to put in doubt", "to question / to cast doubt on", "Non metto in dubbio la tua parola.", "I don't doubt your word.", "❓"],
      ["mettere a posto", "to put in place", "to tidy up / to sort out", "Metti a posto la tua stanza.", "Tidy up your room.", "🧹"],
      ["mettercela tutta", "to put all of it there", "to give it one's all", "Ce l'ha messa tutta.", "She gave it everything.", "💯"],
      ["mettersi d'accordo", "to put oneself in agreement", "to reach an agreement", "Non si mettono d'accordo su niente.", "They can't agree on anything.", "🤝"],
    ],
  },

  // ---- andare (to go) --------------------------------------------------
  {
    category: "andare", rows: [
      ["andare d'accordo", "to go of agreement", "to get along", "Vado d'accordo con i colleghi.", "I get along with my colleagues.", "🤝"],
      ["andare di fretta", "to go in a hurry", "to be in a rush", "Vado di fretta stamattina.", "I'm in a rush this morning.", "🏃"],
      ["andare a male", "to go to bad", "to go off / to spoil", "Il latte è andato a male.", "The milk has gone off.", "🥛"],
      ["andare matto per", "to go crazy for", "to be crazy about", "Va matto per il cioccolato.", "He's crazy about chocolate.", "🍫"],
      ["andarsene", "to go oneself away", "to leave / to go away", "Me ne vado, è tardi.", "I'm off, it's late.", "🚪"],
    ],
  },

  // ---- stare (to stay / to be) ----------------------------------------
  {
    category: "stare", rows: [
      ["stare attento", "to stay attentive", "to be careful / to pay attention", "Stai attento alle macchine.", "Watch out for the cars.", "⚠️"],
      ["starci", "to be there for it", "to be up for it / to be in", "Andiamo al mare? Ci sto!", "Shall we go to the beach? I'm in!", "👍"],
      ["stare zitto", "to stay silent", "to keep quiet", "Stai zitto un attimo.", "Be quiet for a second.", "🤫"],
      ["non stare in piedi", "to not stay on feet", "to not hold up (an argument)", "La tua scusa non sta in piedi.", "Your excuse doesn't hold up.", "🦵"],
    ],
  },

  // ---- idioms & sayings ------------------------------------------------
  {
    category: "idioms", rows: [
      ["costare un occhio della testa", "to cost an eye of the head", "to cost an arm and a leg", "L'auto è costata un occhio della testa.", "The car cost an arm and a leg.", "👁️"],
      ["non vedere l'ora", "to not see the hour", "to can't wait", "Non vedo l'ora di partire.", "I can't wait to leave.", "⏰"],
      ["prendere in giro", "to take around", "to tease / to pull someone's leg", "Mi stai prendendo in giro?", "Are you pulling my leg?", "😜"],
      ["in bocca al lupo", "in the mouth of the wolf", "good luck!", "In bocca al lupo per l'esame!", "Good luck with the exam!", "🐺"],
      ["essere al verde", "to be at the green", "to be broke", "A fine mese sono al verde.", "I'm broke at the end of the month.", "💸"],
      ["rompere il ghiaccio", "to break the ice", "to break the ice", "Una battuta per rompere il ghiaccio.", "A joke to break the ice.", "🧊"],
      ["tagliare la corda", "to cut the rope", "to sneak off / to clear out", "Ha tagliato la corda presto.", "He slipped away early.", "🪢"],
      ["prendere due piccioni con una fava", "to take two pigeons with one bean", "to kill two birds with one stone", "Così prendi due piccioni con una fava.", "That way you kill two birds with one stone.", "🐦"],
      ["avere una fame da lupi", "to have a hunger of wolves", "to be ravenous", "Dopo la corsa ho una fame da lupi.", "After the run I'm ravenous.", "🐺"],
      ["piove sul bagnato", "it rains on the wet", "when it rains, it pours", "Ho perso il treno e pure l'ombrello: piove sul bagnato.", "I missed the train and lost my umbrella too — when it rains, it pours.", "🌧️"],
      ["fare il passo più lungo della gamba", "to take a step longer than the leg", "to bite off more than you can chew", "Non fare il passo più lungo della gamba.", "Don't bite off more than you can chew.", "🦵"],
      ["non avere peli sulla lingua", "to not have hairs on the tongue", "to not mince words / to be blunt", "La mia capa non ha peli sulla lingua.", "My boss doesn't mince her words.", "👅"],
    ],
  },
];

// Category registry — drives the browse view's filter chips and section headers.
// Order here is the canonical section order on the page.
export const EXPRESSION_CATEGORIES = [
  { id: "avere", label: "With avere", emoji: "🫳" },
  { id: "fare", label: "With fare", emoji: "🔨" },
  { id: "dare", label: "With dare", emoji: "🎁" },
  { id: "mettere", label: "With mettere", emoji: "📥" },
  { id: "andare", label: "With andare", emoji: "🚶" },
  { id: "stare", label: "With stare", emoji: "📍" },
  { id: "idioms", label: "Idioms & sayings", emoji: "🎭" },
];

function buildExpressions() {
  const out = [];
  let rank = 1;
  for (const block of GROUPS) {
    for (const [spanish, literal, meaning, exEs, exEn, emoji] of block.rows) {
      out.push({
        spanish,
        literal,
        meaning,
        example_es: exEs,
        example_en: exEn,
        emoji,
        category: block.category,
        rank: rank++,
      });
    }
  }
  return out;
}

// Flattened, ranked list — the single source for the browse view.
export const EXPRESSIONS = buildExpressions();
